<template>
    <div>
        <ts-page-title
            :title="$t('cashTransfer.pageTitle')"
            :breadcrumb="[
                { text: $t('home'), href: '/' },
                { text: $t('cashTransfer.pageTitle'), active: true }
            ]"
        />
        <ts-panel>
            <ts-panel-wrapper
                class="tw-flex tw-items-center tw-justify-between"
            >
                <div class="tw-flex tw-items-center tw-space-x-2">
                    <Button
                        type="info"
                        ghost
                        @click="$router.push({ name: 'cash-transfer-create' })"
                    >
                        {{ $t("addNew") }}
                    </Button>
                    <DatePicker
                        v-model="filter.date_range"
                        type="daterange"
                        format="dd-MM-yyyy"
                        :placeholder="$t('selectDateRange')"
                        style="width: 230px"
                        @on-change="onDateRangeChange"
                        separator=" → "
                    />
                </div>
                <div class="tw-flex tw-justify-end tw-space-x-2">
                    <Input
                        search
                        v-model="search"
                        :placeholder="$t('cashTransfer.search')"
                        :style="width"
                        @on-focus="() => (width = 'width: 500px')"
                        @on-blur="() => (width = 'width: 300px')"
                    />
                    <ts-button
                        v-tooltip="$t('filter')"
                        @click.prevent="visible = true"
                    >
                        <i class="fa fa-filter"></i>
                    </ts-button>
                </div>
            </ts-panel-wrapper>

            <Table
                :columns="columns"
                :data="resources"
                size="small"
                :loading="loading"
                max-height="700"
                highlight-row
                stripe
            >
                <template slot-scope="{ row }" slot="transfer_no">
                    <span class="tw-font-bold text-primary">{{
                        row.transfer_no
                    }}</span>
                </template>

                <template slot-scope="{ row }" slot="branch">
                    {{ row.branch ? row.branch.branch_name_en : "" }}
                </template>

                <template slot-scope="{ row }" slot="transfer_by">
                    <div class="tw-flex tw-items-center tw-gap-2">
                        <span
                            class="tw-inline-flex tw-items-center tw-justify-center tw-w-7 tw-h-7 tw-rounded-full tw-bg-blue-100 tw-text-blue-600 tw-text-xs tw-font-bold tw-flex-shrink-0"
                        >
                            {{
                                row.transfer_by &&
                                (row.transfer_by.employee_name_en ||
                                    row.transfer_by.employee_name_kh)
                                    ? (
                                          row.transfer_by.employee_name_en ||
                                          row.transfer_by.employee_name_kh
                                      )
                                          .charAt(0)
                                          .toUpperCase()
                                    : "?"
                            }}
                        </span>
                        <div class="tw-leading-tight">
                            <div
                                class="tw-font-semibold tw-text-gray-800 tw-text-sm"
                            >
                                {{
                                    row.transfer_by
                                        ? row.transfer_by.employee_name_en ||
                                          row.transfer_by.employee_name_kh
                                        : ""
                                }}
                            </div>
                            <div
                                class="tw-text-xs tw-text-gray-400"
                                v-if="
                                    row.transfer_by &&
                                        row.transfer_by.logicalPosition
                                "
                            >
                                {{
                                    row.transfer_by.logicalPosition
                                        .position_name_en
                                }}
                            </div>
                        </div>
                    </div>
                </template>

                <template slot-scope="{ row }" slot="amount">
                    <span class="tw-font-bold">
                        {{
                            $store.getters.formatCurrencyWithCode(
                                row.transfer_amount,
                                row.currency
                            )
                        }}
                    </span>
                </template>

                <template slot-scope="{ row }" slot="bank">
                    <template v-if="row.bank_name">
                        <div class="tw-flex tw-items-center tw-gap-1 tw-mb-0.5">
                            <i
                                class="fas fa-university tw-text-blue-400"
                                style="font-size:11px;"
                            ></i>
                            <span
                                class="tw-font-semibold tw-text-gray-800"
                                style="font-size:12px;"
                                >{{ row.bank_name }}</span
                            >
                        </div>
                        <div
                            class="tw-flex tw-items-center tw-gap-1 tw-text-gray-500"
                            style="font-size:11px;"
                        >
                            <i
                                class="fas fa-credit-card"
                                style="font-size:10px;color:#9ca3af;"
                            ></i>
                            <span class="tw-font-mono tw-tracking-wide">{{
                                row.bank_account_no
                            }}</span>
                        </div>
                        <div
                            class="tw-flex tw-items-center tw-gap-1 tw-text-gray-400"
                            style="font-size:11px;"
                        >
                            <i
                                class="fas fa-user"
                                style="font-size:10px;color:#d1d5db;"
                            ></i>
                            <span>{{ row.bank_account_name }}</span>
                        </div>
                    </template>
                    <span v-else class="tw-text-gray-300">—</span>
                </template>

                <template slot-scope="{ row }" slot="pending_approvers">
                    <template
                        v-if="
                            row.approval_matrix &&
                                row.approval_matrix.length > 0 &&
                                row.approval_status < 2
                        "
                    >
                        <div
                            v-for="(m, i) in row.approval_matrix.filter(
                                m => m.app_level === row.current_app_level
                            )"
                            :key="i"
                            class="tw-flex tw-items-center tw-gap-1 tw-text-xs tw-leading-snug"
                        >
                            <span
                                class="tw-inline-flex tw-items-center tw-justify-center tw-w-5 tw-h-5 tw-rounded-full tw-text-white tw-text-xs tw-font-bold tw-flex-shrink-0"
                                :class="
                                    m.app_user_id === user_id
                                        ? 'tw-bg-indigo-500'
                                        : m.app_status === 2
                                        ? 'tw-bg-green-500'
                                        : m.app_status === 3
                                        ? 'tw-bg-red-500'
                                        : 'tw-bg-blue-400'
                                "
                            >
                                {{
                                    m.app_user_id === user_id
                                        ? "Y"
                                        : m.employee &&
                                          (m.employee.employee_name_en ||
                                              m.employee.employee_name_kh)
                                        ? (
                                              m.employee.employee_name_en ||
                                              m.employee.employee_name_kh
                                          )
                                              .charAt(0)
                                              .toUpperCase()
                                        : "?"
                                }}
                            </span>
                            <span
                                :class="
                                    m.app_user_id === user_id
                                        ? 'tw-text-indigo-600 tw-font-semibold'
                                        : 'tw-text-gray-700'
                                "
                            >
                                {{
                                    m.app_user_id === user_id
                                        ? "You"
                                        : m.employee
                                        ? m.employee.employee_name_en ||
                                          m.employee.employee_name_kh
                                        : m.app_user_id
                                }}
                            </span>
                            <i
                                :class="
                                    m.app_status === 2
                                        ? 'fas fa-check-circle tw-text-green-500'
                                        : m.app_status === 3
                                        ? 'fas fa-times-circle tw-text-red-500'
                                        : 'fas fa-clock tw-text-gray-400'
                                "
                                style="font-size:10px;"
                            ></i>
                        </div>
                    </template>
                    <span v-else class="tw-text-gray-300">—</span>
                </template>

                <template slot-scope="{ row }" slot="approved_by">
                    <template
                        v-if="
                            row.approval_matrix &&
                                row.approval_matrix.length > 0 &&
                                row.approval_status === 2
                        "
                    >
                        <div
                            v-for="(m, i) in row.approval_matrix.filter(
                                m =>
                                    m.app_level ===
                                        Math.max(
                                            ...row.approval_matrix.map(
                                                x => x.app_level
                                            )
                                        ) && m.app_status === 2
                            )"
                            :key="i"
                            class="tw-flex tw-items-center tw-gap-1 tw-text-xs tw-leading-snug"
                        >
                            <span
                                class="tw-inline-flex tw-items-center tw-justify-center tw-w-5 tw-h-5 tw-rounded-full tw-text-white tw-text-xs tw-font-bold tw-flex-shrink-0"
                                :class="
                                    m.app_user_id === user_id
                                        ? 'tw-bg-indigo-500'
                                        : 'tw-bg-green-500'
                                "
                            >
                                {{
                                    m.app_user_id === user_id
                                        ? "Y"
                                        : m.employee &&
                                          (m.employee.employee_name_en ||
                                              m.employee.employee_name_kh)
                                        ? (
                                              m.employee.employee_name_en ||
                                              m.employee.employee_name_kh
                                          )
                                              .charAt(0)
                                              .toUpperCase()
                                        : "?"
                                }}
                            </span>
                            <span
                                :class="
                                    m.app_user_id === user_id
                                        ? 'tw-text-indigo-600 tw-font-semibold'
                                        : 'tw-text-gray-700 tw-font-medium'
                                "
                            >
                                {{
                                    m.app_user_id === user_id
                                        ? "You"
                                        : m.employee
                                        ? m.employee.employee_name_en ||
                                          m.employee.employee_name_kh
                                        : m.app_user_id
                                }}
                            </span>
                        </div>
                    </template>
                    <span v-else class="tw-text-gray-300">—</span>
                </template>

                <template slot-scope="{ row }" slot="approved_comment">
                    <template v-if="row.approved_comment">
                        <Tooltip
                            :content="row.approved_comment"
                            transfer
                            max-width="260"
                            theme="light"
                        >
                            <span
                                class="tw-text-xs tw-text-gray-600 tw-italic"
                                style="cursor:default;"
                            >
                                {{
                                    row.approved_comment.length > 40
                                        ? row.approved_comment.slice(0, 40) +
                                          "…"
                                        : row.approved_comment
                                }}
                            </span>
                        </Tooltip>
                    </template>
                    <span v-else class="tw-text-gray-300">—</span>
                </template>

                <template slot-scope="{ row }" slot="bank_slip">
                    <a
                        v-if="rowSlipPhotos(row).length"
                        href="#"
                        @click.prevent="onViewSlipPhoto(row)"
                        class="tw-block tw-group"
                    >
                        <div
                            class="tw-relative tw-w-10 tw-h-10 tw-rounded tw-overflow-hidden tw-border tw-border-gray-200 tw-mx-auto"
                        >
                            <img
                                :src="rowSlipPhotos(row)[0]"
                                class="tw-w-full tw-h-full tw-object-cover tw-transition-opacity group-hover:tw-opacity-75"
                            />
                            <div
                                class="tw-absolute tw-inset-0 tw-flex tw-items-center tw-justify-center tw-opacity-0 group-hover:tw-opacity-100 tw-bg-black tw-bg-opacity-30 tw-transition-opacity"
                            >
                                <i
                                    class="fas fa-search-plus tw-text-white tw-text-xs"
                                ></i>
                            </div>
                            <div
                                v-if="rowSlipPhotos(row).length > 1"
                                class="tw-absolute tw-bottom-0 tw-right-0 tw-bg-black tw-bg-opacity-60 tw-text-white tw-text-xs tw-px-1 tw-rounded-tl"
                                style="font-size:9px; line-height:1.4;"
                            >
                                +{{ rowSlipPhotos(row).length - 1 }}
                            </div>
                        </div>
                    </a>
                    <i v-else class="fas fa-times-circle text-danger"></i>
                </template>

                <template slot-scope="{ row }" slot="approval_status">
                    <Poptip
                        trigger="hover"
                        theme="light"
                        transfer
                        v-if="
                            row.approval_matrix &&
                                row.approval_matrix.length > 0 &&
                                row.approval_status < 2
                        "
                    >
                        <template #content>
                            <div style="min-width:180px; padding:4px 0;">
                                <div
                                    v-for="(approvers, level) in approvalLevel(
                                        row.approval_matrix
                                    )"
                                    :key="level"
                                    style="margin-bottom:8px;"
                                >
                                    <div
                                        style="font-size:11px; font-weight:700; color:#6b7280; text-transform:uppercase; letter-spacing:0.05em; margin-bottom:4px;"
                                    >
                                        Level {{ level }}
                                        <span
                                            v-if="
                                                Number(level) ===
                                                    row.current_app_level
                                            "
                                            style="color:#3b82f6; margin-left:4px;"
                                            >(current)</span
                                        >
                                    </div>
                                    <div
                                        v-for="(u, i) in approvers"
                                        :key="i"
                                        style="display:flex; align-items:center; gap:6px; padding:2px 4px; border-radius:4px;"
                                    >
                                        <span
                                            style="display:inline-flex; align-items:center; justify-content:center; width:20px; height:20px; border-radius:50%; color:#fff; font-size:10px; font-weight:700; flex-shrink:0;"
                                            :style="{
                                                background:
                                                    u.app_user_id === user_id
                                                        ? '#6366f1'
                                                        : u.app_status === 2
                                                        ? '#22c55e'
                                                        : u.app_status === 3
                                                        ? '#ef4444'
                                                        : '#9ca3af'
                                            }"
                                            >{{
                                                u.app_user_id === user_id
                                                    ? "Y"
                                                    : u.employee &&
                                                      (u.employee
                                                          .employee_name_en ||
                                                          u.employee
                                                              .employee_name_kh)
                                                    ? (
                                                          u.employee
                                                              .employee_name_en ||
                                                          u.employee
                                                              .employee_name_kh
                                                      )
                                                          .charAt(0)
                                                          .toUpperCase()
                                                    : "?"
                                            }}</span
                                        >
                                        <span
                                            style="font-size:12px; flex:1; white-space:nowrap;"
                                            :style="{
                                                color:
                                                    u.app_user_id === user_id
                                                        ? '#4f46e5'
                                                        : '#374151',
                                                fontWeight:
                                                    u.app_user_id === user_id
                                                        ? '600'
                                                        : '400'
                                            }"
                                        >
                                            {{
                                                u.app_user_id === user_id
                                                    ? "You"
                                                    : u.employee
                                                    ? u.employee
                                                          .employee_name_en ||
                                                      u.employee
                                                          .employee_name_kh
                                                    : u.app_user_id
                                            }}
                                        </span>
                                        <i
                                            style="font-size:11px; flex-shrink:0;"
                                            :class="
                                                u.app_status === 2
                                                    ? 'fas fa-check-circle'
                                                    : u.app_status === 3
                                                    ? 'fas fa-times-circle'
                                                    : 'fas fa-clock'
                                            "
                                            :style="{
                                                color:
                                                    u.app_status === 2
                                                        ? '#22c55e'
                                                        : u.app_status === 3
                                                        ? '#ef4444'
                                                        : '#d1d5db'
                                            }"
                                        ></i>
                                    </div>
                                </div>
                            </div>
                        </template>
                        <div class="tw-inline-flex tw-items-center tw-gap-1">
                            <span :class="statusStyle(row.approval_status)">{{
                                statusLabel(row.approval_status)
                            }}</span>
                            <span
                                class="badge bg-secondary rounded-pill tw-text-xs"
                                v-if="row.approval_status < 2"
                                >Lv {{ row.current_app_level }}</span
                            >
                        </div>
                    </Poptip>
                    <span v-else :class="statusStyle(row.approval_status)">
                        {{ statusLabel(row.approval_status) }}
                    </span>
                </template>

                <template slot-scope="{ row }" slot="action">
                    <div
                        class="tw-flex tw-items-center tw-justify-center tw-space-x-2"
                    >
                        <a
                            v-if="canEditRow(row)"
                            href="#"
                            class="text-primary"
                            @click.prevent="onEdit(row)"
                            v-tooltip="$t('edit')"
                        >
                            <Icon type="ios-create" size="20" />
                        </a>

                        <a
                            v-if="canApprove(row)"
                            href="#"
                            class="text-warning"
                            @click.prevent="onApprovalModal(row, 3)"
                            v-tooltip="$t('cashTransfer.reject')"
                        >
                            <Icon type="ios-close-circle" size="20" />
                        </a>

                        <a
                            v-if="canApprove(row)"
                            href="#"
                            class="text-info"
                            @click.prevent="onApprovalModal(row, 2)"
                            v-tooltip="$t('cashTransfer.approve')"
                        >
                            <Icon type="ios-checkmark-circle" size="20" />
                        </a>

                        <!-- Approval History -->
                        <a
                            href="#"
                            class="text-secondary"
                            @click.prevent="onViewHistory(row)"
                            v-tooltip="$t('cashTransfer.approvalHistory')"
                        >
                            <i
                                class="fas fa-history"
                                style="font-size:16px;"
                            ></i>
                        </a>

                        <Poptip
                            v-if="canDeleteRow(row)"
                            :title="$t('are_you_sure_to_delete')"
                            :transfer="true"
                            width="200"
                        >
                            <a
                                class="text-danger"
                                v-tooltip="$t('delete')"
                                :disabled="row._deleting"
                            >
                                <i
                                    class="fas fa-circle-notch fa-spin"
                                    v-if="row._deleting"
                                ></i>
                                <Icon type="ios-trash" size="20" v-else />
                            </a>
                            <div slot="content" class="tw-text-right tw-mt-2">
                                <a
                                    class="ivu-btn ivu-btn-danger ivu-btn-small"
                                    @click.prevent="onDelete(row)"
                                >
                                    <span>{{ $t("ok") }}</span>
                                </a>
                            </div>
                        </Poptip>
                    </div>
                </template>
            </Table>

            <div class="tw-flex tw-items-center tw-justify-end tw-p-4">
                <ts-i-pagination
                    v-model="pagination"
                    @navigate="fetchData"
                ></ts-i-pagination>
            </div>
        </ts-panel>

        <!-- Filter Drawer -->
        <Drawer
            :title="$t('filter')"
            :closable="false"
            :value="visible"
            @on-close="() => (visible = false)"
            width="300px"
        >
            <div class="mb-3">
                <label class="form-col-label control-label">{{
                    $t("branchName")
                }}</label>
                <ts-branch-filter
                    @filter="value => (filter.branch_id = value)"
                />
            </div>
            <div class="mb-3">
                <label class="form-col-label control-label">{{
                    $t("cashTransfer.status")
                }}</label>
                <Select
                    v-model="filter.approval_status"
                    multiple
                    clearable
                    :placeholder="$t('all')"
                >
                    <Option :value="0">{{
                        $t("cashTransfer.pending")
                    }}</Option>
                    <Option :value="1">{{
                        $t("cashTransfer.partiallyApproved")
                    }}</Option>
                    <Option :value="2">{{
                        $t("cashTransfer.approved")
                    }}</Option>
                    <Option :value="3">{{
                        $t("cashTransfer.rejected")
                    }}</Option>
                </Select>
            </div>
        </Drawer>

        <!-- Approve / Reject Modal -->
        <Modal
            v-model="approvalModal"
            :width="
                approvalRow && isLastLevelApprover(approvalRow) ? '480' : '320'
            "
            @on-cancel="onApprovalCancel"
            :loading="approving"
        >
            <p slot="header" style="color:#f60; text-align:center">
                <Icon type="ios-information-circle" />
                <span>{{
                    approval.approve_status_id === 2
                        ? $t("cashTransfer.areYouSureApprove")
                        : $t("cashTransfer.areYouSureReject")
                }}</span>
            </p>
            <div>
                <label :class="{ required: approval.approve_status_id === 3 }">
                    {{ $t("cashTransfer.reason") }}
                </label>
                <textarea
                    v-model.trim="approval.approval_comment"
                    class="form-control"
                    :class="{ 'is-invalid': errors.has('approval_comment') }"
                    rows="2"
                ></textarea>
                <div
                    class="invalid-feedback"
                    v-if="errors.has('approval_comment')"
                >
                    {{ errors.first("approval_comment") }}
                </div>

                <!-- Bank slip read-only view -->
                <template v-if="approvalRow && rowSlipPhotos(approvalRow).length">
                    <div
                        class="tw-flex tw-items-center tw-gap-2 tw-mt-4 tw-mb-3"
                    >
                        <div
                            class="tw-flex-1 tw-border-t tw-border-gray-200"
                        ></div>
                        <span
                            class="tw-text-xs tw-font-semibold tw-text-gray-500 tw-uppercase tw-tracking-wide tw-whitespace-nowrap"
                        >
                            {{ $t("cashTransfer.bankSlip") }}
                        </span>
                        <div
                            class="tw-flex-1 tw-border-t tw-border-gray-200"
                        ></div>
                    </div>
                    <div
                        class="tw-rounded-lg tw-border tw-border-gray-200 tw-bg-gray-50 tw-p-3 tw-space-y-2"
                    >
                        <div class="tw-flex tw-flex-wrap tw-gap-2 tw-mb-2">
                            <a
                                v-for="(photo, i) in rowSlipPhotos(approvalRow)"
                                :key="i"
                                href="#"
                                @click.prevent="onViewSlipPhotos(rowSlipPhotos(approvalRow), i)"
                            >
                                <img
                                    :src="photo"
                                    class="tw-w-14 tw-h-14 tw-object-cover tw-rounded tw-border tw-border-gray-200 tw-flex-shrink-0"
                                />
                            </a>
                        </div>
                        <div>
                            <div class="tw-text-xs tw-text-gray-500">
                                {{ $t("cashTransfer.bankSlipDate") }}
                            </div>
                            <div
                                class="tw-font-semibold tw-text-gray-800 tw-text-sm"
                            >
                                {{ approvalRow.bank_slip_date || "—" }}
                            </div>
                            <div
                                v-if="approvalRow.bank_slip_remark"
                                class="tw-text-xs tw-text-gray-500 tw-mt-1"
                            >
                                {{ approvalRow.bank_slip_remark }}
                            </div>
                        </div>
                    </div>
                </template>
            </div>
            <div slot="footer">
                <Button
                    :type="
                        approval.approve_status_id === 2 ? 'info' : 'warning'
                    "
                    size="large"
                    long
                    @click="onApproval"
                    :loading="approving"
                >
                    {{ $t("yes") }}
                </Button>
            </div>
        </Modal>

        <!-- Bank Slip Modal -->
        <Modal
            v-model="bankSlipModal"
            width="420"
            :title="$t('cashTransfer.bankSlip')"
            @on-cancel="onBankSlipCancel"
        >
            <div class="row">
                <div class="col-md-12 tw-mb-3">
                    <label class="required form-col-label control-label">
                        {{ $t("cashTransfer.bankSlipPhoto") }}
                    </label>
                    <bank-slip-photos-upload
                        :value="bankSlip.bank_slip_photos"
                        :loading="uploadingPhoto"
                        :error="errors.first('bank_slip_photos') || errors.first('bank_slip_photos.0')"
                        @add="onSlipFilesAdd"
                        @remove="onSlipRemove"
                        @preview="idx => onViewSlipPhotos(bankSlip.bank_slip_photos, idx)"
                    />
                </div>

                <div class="col-md-12 tw-mb-3">
                    <label class="required form-col-label control-label">
                        {{ $t("cashTransfer.bankSlipDate") }}
                    </label>
                    <DatePicker
                        v-model="bankSlip.bank_slip_date"
                        type="date"
                        format="dd-MM-yyyy"
                        style="width: 100%"
                        :class="{ 'is-invalid': errors.has('bank_slip_date') }"
                    />
                    <div
                        class="invalid-feedback"
                        v-if="errors.has('bank_slip_date')"
                    >
                        {{ errors.first("bank_slip_date") }}
                    </div>
                </div>

                <div class="col-md-12 tw-mb-3">
                    <label class="form-col-label control-label">
                        {{ $t("cashTransfer.bankSlipRemark") }}
                    </label>
                    <textarea
                        v-model.trim="bankSlip.bank_slip_remark"
                        class="form-control"
                        rows="2"
                    ></textarea>
                </div>
            </div>
            <div slot="footer">
                <Button @click="onBankSlipCancel">{{
                    $t("cancel")
                }}</Button>
                <Button
                    type="primary"
                    :loading="savingBankSlip"
                    @click="onSaveBankSlip"
                >
                    {{ $t("save") }}
                </Button>
            </div>
        </Modal>

        <!-- Approval History Modal -->
        <Modal
            v-model="historyModal.visible"
            width="560"
            :footer-hide="true"
            :title="$t('cashTransfer.approvalHistory')"
        >
            <approval-matrix-expand :matrix="historyModal.matrix" />
        </Modal>

        <!-- Bank Slip Photo Viewer -->
        <Modal
            v-model="slipPhotoModal.visible"
            width="600"
            :footer-hide="true"
            :title="$t('cashTransfer.bankSlip')"
        >
            <div class="tw-relative">
                <img
                    v-if="slipCurrent"
                    :src="slipCurrent"
                    class="tw-w-full tw-rounded-lg tw-shadow tw-block"
                    style="max-height: 75vh; object-fit: contain; background: #f3f4f6;"
                />
                <button
                    v-if="slipPhotoModal.photos.length > 1"
                    type="button"
                    class="slip-nav slip-nav-prev"
                    @click="slipNav(-1)"
                >
                    <i class="fas fa-chevron-left"></i>
                </button>
                <button
                    v-if="slipPhotoModal.photos.length > 1"
                    type="button"
                    class="slip-nav slip-nav-next"
                    @click="slipNav(1)"
                >
                    <i class="fas fa-chevron-right"></i>
                </button>
                <div
                    v-if="slipPhotoModal.photos.length > 1"
                    class="tw-absolute tw-top-2 tw-right-2 tw-bg-black tw-bg-opacity-60 tw-text-white tw-text-xs tw-px-2 tw-py-1 tw-rounded"
                >
                    {{ slipPhotoModal.currentIndex + 1 }} / {{ slipPhotoModal.photos.length }}
                </div>
            </div>
            <div
                v-if="slipPhotoModal.photos.length > 1"
                class="tw-flex tw-flex-wrap tw-gap-2 tw-mt-3 tw-justify-center"
            >
                <img
                    v-for="(p, i) in slipPhotoModal.photos"
                    :key="i"
                    :src="p"
                    class="tw-w-12 tw-h-12 tw-rounded tw-object-cover tw-cursor-pointer tw-border-2"
                    :class="i === slipPhotoModal.currentIndex ? 'tw-border-blue-500' : 'tw-border-transparent'"
                    @click="slipPhotoModal.currentIndex = i"
                />
            </div>
            <div
                v-if="slipPhotoModal.date"
                class="tw-mt-3 tw-text-center tw-text-sm tw-text-gray-500"
            >
                <i class="fas fa-calendar-alt tw-mr-1"></i
                >{{ slipPhotoModal.date }}
            </div>
        </Modal>
    </div>
</template>

<script>
import { mapState } from "vuex";
import { debounce, groupBy } from "lodash";
import { Errors } from "form-backend-validation";
import moment from "moment";
import BankSlipPhotosUpload from "../components/BankSlipPhotosUpload.vue";
import ApprovalMatrixExpand from "../components/ApprovalMatrixExpand.vue";

export default {
    name: "CashTransferIndex",
    components: { BankSlipPhotosUpload, ApprovalMatrixExpand },
    data() {
        return {
            loading: false,
            errors: new Errors(),
            width: "width: 300px",
            visible: false,
            approvalModal: false,
            approving: false,
            approvalRow: null,
            approval: {
                transfer_id: null,
                approve_status_id: null,
                approval_comment: ""
            },
            bankSlipModal: false,
            savingBankSlip: false,
            uploadingPhoto: false,
            bankSlip: {
                transfer_id: null,
                bank_slip_photos: [],
                bank_slip_date: moment(new Date()).format("DD-MM-YYYY"),
                bank_slip_remark: ""
            },
            filter: {
                branch_id: [],
                approval_status: [],
                date_range: [
                    new Date(),
                    new Date(new Date().getTime() + 86400000)
                ]
            },
            slipPhotoModal: {
                visible: false,
                photos: [],
                currentIndex: 0,
                date: null
            },
            historyModal: {
                visible: false,
                matrix: []
            }
        };
    },
    computed: {
        ...mapState("finance/cashTransfer", ["resources", "pagination"]),
        slipCurrent() {
            const { photos, currentIndex } = this.slipPhotoModal;
            return photos && photos.length ? photos[currentIndex] : null;
        },
        user_id() {
            return this.$store.state.authUser.user.user_id;
        },
        myEmployeeId() {
            return this.$store.state.authUser.user.employee_id;
        },
        search: {
            get() {
                return this.$store.state.finance.cashTransfer.search;
            },
            set(value) {
                this.$store.commit("finance/cashTransfer/SET_SEARCH", value);
                this.$store.commit("finance/cashTransfer/RESET_CURRENT_PAGE");
            }
        },
        columns() {
            return [
                {
                    title: this.$t("cashTransfer.transferNo"),
                    slot: "transfer_no",
                    fixed: "left",
                    minWidth: 180
                },
                {
                    title: this.$t("cashTransfer.branch"),
                    slot: "branch",
                    minWidth: 130
                },
                {
                    title: this.$t("cashTransfer.transferBy"),
                    slot: "transfer_by",
                    minWidth: 200
                },
                {
                    title: this.$t("cashTransfer.transferDate"),
                    key: "transfer_date",
                    minWidth: 120,
                    align: "center"
                },
                {
                    title: this.$t("cashTransfer.amount"),
                    slot: "amount",
                    minWidth: 150,
                    align: "right"
                },
                {
                    title: this.$t("cashTransfer.bank"),
                    slot: "bank",
                    minWidth: 180
                },
                {
                    title: this.$t("cashTransfer.pendingApprovers"),
                    slot: "pending_approvers",
                    minWidth: 220
                },
                {
                    title: this.$t("cashTransfer.approvedBy"),
                    slot: "approved_by",
                    minWidth: 160
                },
                {
                    title: this.$t("cashTransfer.approvedComment"),
                    slot: "approved_comment",
                    minWidth: 180
                },
                {
                    title: this.$t("cashTransfer.bankSlip"),
                    slot: "bank_slip",
                    minWidth: 90,
                    align: "center"
                },
                {
                    title: this.$t("cashTransfer.status"),
                    slot: "approval_status",
                    minWidth: 150,
                    fixed: "right",
                    align: "center"
                },
                {
                    title: this.$t("actions"),
                    slot: "action",
                    fixed: "right",
                    align: "center",
                    width: 140
                }
            ];
        }
    },
    methods: {
        fetchData(attributes = {}) {
            this.loading = true;
            const [dateFrom, dateTo] = this.filter.date_range || [];
            this.$store
                .dispatch("finance/cashTransfer/fetch", {
                    ...attributes,
                    filter: {
                        search: this.search,
                        branch_id: this.filter.branch_id,
                        approval_status: this.filter.approval_status,
                        date_from: dateFrom
                            ? moment(dateFrom).format("YYYY-MM-DD")
                            : null,
                        date_to: dateTo
                            ? moment(dateTo).format("YYYY-MM-DD")
                            : null
                    }
                })
                .finally(() => (this.loading = false));
        },

        onDateRangeChange() {
            this.fetchData();
        },
        onEdit(row) {
            this.$store.commit("finance/cashTransfer/SET_EDIT_DATA", row);
            this.$router.push({
                name: "cash-transfer-edit",
                params: { id: row.transfer_id }
            });
        },
        onDelete(row) {
            row._deleting = true;
            this.$store
                .dispatch("finance/cashTransfer/destroy", row.transfer_id)
                .then(response => {
                    this.notice({
                        type: response.message_id == 0 ? "success" : "warning",
                        text: response.message
                    });
                    this.fetchData();
                })
                .catch(error =>
                    this.notice({ type: "error", text: error.message })
                )
                .finally(() => (row._deleting = false));
        },
        isLastLevelApprover(row) {
            if (!row.approval_matrix || row.approval_matrix.length === 0)
                return false;
            const maxLevel = Math.max(
                ...row.approval_matrix.map(m => m.app_level)
            );
            return row.approval_matrix.some(
                m => m.app_user_id === this.user_id && m.app_level === maxLevel
            );
        },
        hasPermission(name) {
            const perms = this.$store.state.authUser.permissions || [];
            return perms.includes(name);
        },
        /**
         * Edit allowed when the row is pending AND the transferrer is me, OR
         * the user has the override permission `update-cash-transfers`.
         */
        canEditRow(row) {
            if (this.hasPermission("update-cash-transfers")) return true;
            return (
                row.approval_status === 0 &&
                row.transfer_by &&
                row.transfer_by.employee_id === this.myEmployeeId
            );
        },
        /**
         * Delete allowed under same rules as edit, with override permission
         * `delete-cash-transfers`.
         */
        canDeleteRow(row) {
            if (this.hasPermission("delete-cash-transfers")) return true;
            return (
                row.approval_status === 0 &&
                row.transfer_by &&
                row.transfer_by.employee_id === this.myEmployeeId
            );
        },
        canApprove(row) {
            if (row.approval_status > 1) return false;
            if (!row.approval_matrix || row.approval_matrix.length === 0)
                return false;
            return row.approval_matrix.some(
                m =>
                    m.app_user_id === this.user_id &&
                    m.app_level === row.current_app_level &&
                    m.app_status === 0
            );
        },
        onApprovalModal(row, statusId) {
            this.approvalRow = row;
            this.approval.transfer_id = row.transfer_id;
            this.approval.approve_status_id = statusId;
            this.approval.approval_comment = "";
            this.errors = new Errors();
            this.approvalModal = true;
        },
        onApprovalCancel() {
            this.approvalModal = false;
            this.approvalRow = null;
        },
        onApproval() {
            this.approving = true;
            this.errors = new Errors();
            this.$store
                .dispatch("finance/cashTransfer/approveOrReject", {
                    id: this.approval.transfer_id,
                    data: this.approval
                })
                .then(response => {
                    this.notice({
                        type: response.message_id == 0 ? "success" : "warning",
                        text: response.message
                    });
                    this.approvalModal = false;
                    this.fetchData();
                })
                .catch(error => {
                    this.errors = new Errors(error.errors);
                    this.notice({ type: "error", text: error.message });
                })
                .finally(() => (this.approving = false));
        },
        rowSlipPhotos(row) {
            if (Array.isArray(row.bank_slip_photos) && row.bank_slip_photos.length) {
                return row.bank_slip_photos;
            }
            return row.bank_slip_photo ? [row.bank_slip_photo] : [];
        },
        uploadPhotos(files) {
            const uploads = files.map(file => {
                const formData = new FormData();
                formData.append("photo", file);
                return this.$store
                    .dispatch("finance/cashTransfer/photoUpload", formData)
                    .then(path => (path ? `/api/files?filePath=${path}` : null));
            });
            return Promise.all(uploads);
        },
        onBankSlipModal(row) {
            this.bankSlip.transfer_id = row.transfer_id;
            this.bankSlip.bank_slip_photos = this.rowSlipPhotos(row).slice();
            this.bankSlip.bank_slip_date = row.bank_slip_date
                ? moment(row.bank_slip_date).format("DD-MM-YYYY")
                : moment(new Date()).format("DD-MM-YYYY");
            this.bankSlip.bank_slip_remark = row.bank_slip_remark || "";
            this.errors = new Errors();
            this.bankSlipModal = true;
        },
        onBankSlipCancel() {
            this.bankSlipModal = false;
            this.bankSlip.bank_slip_photos = [];
        },
        onSlipFilesAdd(files) {
            this.uploadingPhoto = true;
            this.uploadPhotos(files)
                .then(paths => {
                    this.bankSlip.bank_slip_photos = [
                        ...this.bankSlip.bank_slip_photos,
                        ...paths.filter(p => !!p)
                    ];
                })
                .catch(error =>
                    this.notice({ type: "error", text: error.message })
                )
                .finally(() => (this.uploadingPhoto = false));
        },
        onSlipRemove(index) {
            this.bankSlip.bank_slip_photos.splice(index, 1);
        },
        onSaveBankSlip() {
            this.savingBankSlip = true;
            this.errors = new Errors();
            this.$store
                .dispatch("finance/cashTransfer/bankSlip", {
                    id: this.bankSlip.transfer_id,
                    data: this.bankSlip
                })
                .then(response => {
                    this.notice({
                        type: response.message_id == 0 ? "success" : "warning",
                        text: response.message
                    });
                    this.bankSlipModal = false;
                    this.fetchData();
                })
                .catch(error => {
                    this.errors = new Errors(error.errors);
                    this.notice({ type: "error", text: error.message });
                })
                .finally(() => (this.savingBankSlip = false));
        },
        onViewHistory(row) {
            this.historyModal.matrix = row.approval_matrix || [];
            this.historyModal.visible = true;
        },
        onViewSlipPhoto(row) {
            const photos = this.rowSlipPhotos(row);
            this.slipPhotoModal.photos = photos;
            this.slipPhotoModal.currentIndex = 0;
            this.slipPhotoModal.date = row.bank_slip_date
                ? moment(row.bank_slip_date).format("DD-MM-YYYY")
                : null;
            this.slipPhotoModal.visible = photos.length > 0;
        },
        onViewSlipPhotos(photos, index = 0) {
            this.slipPhotoModal.photos = photos.slice();
            this.slipPhotoModal.currentIndex = index;
            this.slipPhotoModal.date = null;
            this.slipPhotoModal.visible = photos.length > 0;
        },
        slipNav(delta) {
            const total = this.slipPhotoModal.photos.length;
            if (!total) return;
            this.slipPhotoModal.currentIndex =
                (this.slipPhotoModal.currentIndex + delta + total) % total;
        },
        approvalLevel(matrix) {
            return groupBy(matrix, "app_level");
        },
        statusLabel(status) {
            const map = {
                0: "Pending",
                1: "Partially Approved",
                2: "Approved",
                3: "Rejected"
            };
            return map[status] || status;
        },
        statusStyle(status) {
            const map = {
                0: "badge bg-warning rounded-pill",
                1: "badge bg-info rounded-pill",
                2: "badge bg-success rounded-pill",
                3: "badge bg-danger rounded-pill"
            };
            return map[status] || "badge bg-secondary rounded-pill";
        },
        notice(not) {
            this.$Notice[not.type]({
                title: "CASH TRANSFER",
                desc: not.text
            });
        }
    },
    watch: {
        search: debounce(function() {
            this.fetchData();
        }, 500),
        "filter.branch_id"() {
            this.fetchData();
        },
        "filter.approval_status"() {
            this.fetchData();
        }
    }
};
</script>

<style scoped>
.slip-nav {
    position: absolute;
    top: 50%;
    transform: translateY(-50%);
    background: rgba(0, 0, 0, 0.5);
    color: #fff;
    border: none;
    border-radius: 50%;
    width: 32px;
    height: 32px;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    font-size: 13px;
    transition: background 0.15s;
    z-index: 10;
}
.slip-nav:hover {
    background: rgba(0, 0, 0, 0.75);
}
.slip-nav-prev { left: 8px; }
.slip-nav-next { right: 8px; }
</style>
